<script setup lang="ts">
    import {ref} from 'vue';
    import {formatLicensePlate, normalizeLicensePlate} from './licensePlate';
    import {useSearchSubmit} from './useSearchSubmit';

    const props = defineProps<{disabled: boolean}>();

    const emit = defineEmits<{(event: 'search', plate: string): void}>();

    const plate = ref('');

    function formatInput(event: Event): void {
        plate.value = formatLicensePlate((event.target as HTMLInputElement).value);
    }

    // Zoek stays enabled on an empty field: the design shows it black at rest, and a customer who
    // hits it before typing is asking for the plate box, not for a dead button.
    const {field, submit} = useSearchSubmit(
        () => props.disabled,
        () => plate.value,
        normalizeLicensePlate,
        (value) => emit('search', value)
    );
</script>

<template>
    <form
        class="plate-search"
        @submit.prevent="submit">
        <div class="plate-search__plate">
            <span class="c-license-plate__country-code--small">NL</span>
            <input
                ref="field"
                :value="plate"
                class="c-license-plate__input--small"
                type="text"
                name="license_plate"
                maxlength="12"
                autocomplete="off"
                autocapitalize="characters"
                spellcheck="false"
                aria-label="Kenteken"
                placeholder="XX-XXX-X"
                :disabled="disabled"
                @input="formatInput" />
        </div>
        <button
            type="submit"
            class="c-button--black--width-unchanged plate-search__submit"
            :disabled="disabled">
            <span>Zoek</span>
            <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.6"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true">
                <path d="M9 18l6-6-6-6" />
            </svg>
        </button>
    </form>
</template>

<style scoped>
    .plate-search {
        display: flex;
        gap: 8px;
    }

    .plate-search__plate {
        display: flex;
        flex: 1 1 auto;
        min-width: 0;
        overflow: hidden;
        border-radius: 4px;
    }

    /* Keep the existing 40px plate controls in the flex row. */
    .plate-search .c-license-plate__country-code--small {
        flex-shrink: 0;
        float: none;
    }

    .plate-search .c-license-plate__input--small {
        flex: 1 1 auto;
        width: auto;
        min-width: 0;
    }

    .plate-search .c-license-plate__input--small:disabled {
        opacity: 0.6;
    }

    .plate-search__submit {
        flex: 0 0 auto;
    }
</style>
